import { ChangeDetectionStrategy, Component, Input, OnInit, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, switchMap } from 'rxjs';
import { ManagedUserStatusEnum, ManagedUserStatusTranslationMap } from '../../core/enums/managed-user-status.enum';
import { ManagedUserFilterParamEnum, ManagedUsersListRowInterface } from '../../core/models/managed-user.model';
import { UserPermissionOptionsInterface } from '../../core/models/user-permissions.model';
import { FilterableListCellDirective } from '../../filterable-list/filterable-list-cell.directive';
import { FilterableListColumnDirective } from '../../filterable-list/filterable-list-column.directive';
import { FilterableListHeaderCellDirective } from '../../filterable-list/filterable-list-header-cell.directive';
import { FilterableListComponent } from '../../filterable-list/filterable-list.component';
import { GenericFilterableListComponent } from '../../filterable-list/filterable-list/filterable-list.component';
import { FilterableListCheckboxOptionComponent } from '../../filterable-list/filterable-list/header-components/filterable-list-checkbox-search/filterable-list-checkbox-option/filterable-list-checkbox-option.component';
import {
  FilterableListCheckboxSearchComponent,
  FilterableListCheckboxValue,
} from '../../filterable-list/filterable-list/header-components/filterable-list-checkbox-search/filterable-list-checkbox-search.component';
import { FilterableListTextHeaderComponent } from '../../filterable-list/filterable-list/header-components/filterable-list-text-header/filterable-list-text-header.component';
import { TagComponent } from '../../shared/components/tag/tag.component';
import { ImageSizeEnum } from '../../shared/pipes/resize-image/resize-image.pipe';
import { SharedModule } from '../../shared/shared.module';
import { ToastService } from '../../ui-elements/toast/toast.service';
import { EditManagedUserModalComponent } from '../modals/edit-managed-user-modal/edit-managed-user-modal.component';
import { ManagedUsersSearchService } from '../services/managed-users-search.service';
import { ManagedUsersService } from '../services/managed-users.service';
import { ManagedUsersListActionsComponent } from './managed-users-list-actions/managed-users-list-actions.component';

@Component({
  selector: 'app-managed-users-list',
  imports: [
    SharedModule,
    FilterableListColumnDirective,
    FilterableListTextHeaderComponent,
    FilterableListCellDirective,
    GenericFilterableListComponent,
    FilterableListHeaderCellDirective,
    FilterableListCheckboxSearchComponent,
    FilterableListCheckboxOptionComponent,
    TagComponent,
    ManagedUsersListActionsComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './list.component.html',
})
export class ManagedUsersListComponent extends FilterableListComponent<ManagedUsersListRowInterface> implements OnInit {
  protected service = inject(ManagedUsersService);
  private managedUsersSearchService = inject(ManagedUsersSearchService);
  private toastService = inject(ToastService);
  private translator = inject(TranslateService);

  @Input() loading$: Observable<boolean>;
  @Input() refresh$ = new Observable<void>();
  @Input() permissionOptions: UserPermissionOptionsInterface;

  managedUsersFilterParamEnum = ManagedUserFilterParamEnum;
  managedUserStatus = ManagedUserStatusEnum;
  managedUserStatusTranslationMap = ManagedUserStatusTranslationMap;
  managedUserFilterParamEnum = ManagedUserFilterParamEnum;
  imageSizes = ImageSizeEnum;
  editUserModalOpened = false;

  searchIsActive = false;
  filteringIsActive = false;

  ngOnInit(): void {
    super.ngOnInit();

    this.subscriptions.add(
      this.managedUsersSearchService.searchActive$.subscribe((searchActive) => {
        this.searchIsActive = searchActive;

        // fetch rows initially and on search field clear
        if (!searchActive) {
          this.reloadInitialRows();
        }
      }),
    );

    this.subscriptions.add(
      this.loading$.subscribe((searchIsLoading) => {
        this.loading = searchIsLoading;
      }),
    );

    this.subscriptions.add(
      this.refresh$.subscribe(() => {
        this.refreshList();
      }),
    );
  }

  checkFilterChange(event: FilterableListCheckboxValue) {
    this.filteringIsActive = !!event.value.length;

    this.page = 1;
    // if both checkboxes are checked - dont send filter values to BE
    this.searchParams[event.param] = event.value.length === 1 ? event.value : [];
    this.filterEmit.emit(this.searchParams);

    if (!this.managedUsersSearchService.isSearchActive()) {
      this.filterAll();
    }
  }

  getUserName(row: ManagedUsersListRowInterface): string {
    const { firstName, lastName } = row;

    if (!firstName && !lastName) {
      return '-';
    }

    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    }

    return firstName || lastName;
  }

  onSelect(row: ManagedUsersListRowInterface): void {
    if (this.editUserModalOpened || !this.permissionOptions) {
      return;
    }

    this.editUserModalOpened = true;

    this.service.getOne(row.id).subscribe((user) => {
      const modalRef = this.modalService.open(EditManagedUserModalComponent, {
        size: 'lg',
        centered: true,
        backdrop: 'static',
        keyboard: false,
        beforeDismiss: () => {
          this.editUserModalOpened = false;
          return true;
        },
      });

      const instance = modalRef.componentInstance as EditManagedUserModalComponent;
      instance.user = user;
      instance.username = this.getUserName(user);
      instance.permissionOptions = this.permissionOptions;

      instance.saved.subscribe(() => {
        this.refreshList();
      });
    });
  }

  refreshList(): void {
    if (this.searchIsActive) {
      // filter emission will retrigger search request call
      this.filterEmit.emit(this.searchParams);
    } else {
      this.filterAll();
    }
  }

  onResendPasswordLink(userId: number): void {
    this.service
      .resendPasswordLink(userId)
      .pipe(switchMap(() => this.translator.get('MANAGED_USERS.PASSWORD_LINK_SENT')))
      .subscribe((translation) => {
        this.toastService.success(translation);
      });
  }

  onSetUserBlocked(userId: number, blocked: boolean): void {
    this.service
      .update(userId, { isActive: !blocked })
      .pipe(switchMap(() => this.translator.get(`MANAGED_USERS.${blocked ? 'ACCOUNT_BLOCKED' : 'ACCOUNT_UNBLOCKED'}`)))
      .subscribe((translation) => {
        this.toastService.success(translation);
        this.refreshList();
      });
  }
}
